import React, { useEffect, useState } from 'react'

import CheckIcon from '@mui/icons-material/Check'
import RubbishBinIcon from '@mui/icons-material/DeleteForever'
import Help from '@mui/icons-material/Help'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import Stack from '@mui/material/Stack'
import ToggleButton from '@mui/material/ToggleButton'
import Tooltip from '@mui/material/Tooltip'
import OLStyleCircle from 'ol/style/Circle'
import OLStyleFill from 'ol/style/Fill'
import OLStyleRegularShape from 'ol/style/RegularShape'
import OLStyleStroke from 'ol/style/Stroke'
import OLStyleStyle from 'ol/style/Style'
import OLStyleText from 'ol/style/Text'
import { useSelector } from 'react-redux'

import type { RootState } from 'reduxStore'

import * as Model from 'api/model'
import AccordionWrapper from 'drawer/tabs/AccordionWrapper'
import type { StyleCollection, MeasurementToolTips } from 'drawer/tabs/tools/MeasurementTool'
import MeasurementTool from 'drawer/tabs/tools/MeasurementTool'
import measureAreaImg from 'img/tools/measure-area-noun-22504.svg'
import measureDistanceImg from 'img/tools/measure-distance-noun-412185.svg'

const styles: { [key: string]: React.CSSProperties } = {
  root: {
    padding: '10px 0px 15px 26px'
  },
  checkBoxContainer: {
    padding: '10px 0px'
  },
  helpButton: {
    color: '#737373',
    cursor: 'pointer',
    padding: '0px 8px',
    width: 36
  }
}

const measurementStyles: StyleCollection = {
  defaultPointStyle: new OLStyleStyle({
    image: new OLStyleCircle({
      radius: 4,
      stroke: new OLStyleStroke({ color: 'white' }),
      fill: new OLStyleFill({ color: 'black' })
    })
  }),
  defaultLineStyle: [new OLStyleStyle({
    stroke: new OLStyleStroke({
      color: 'white',
      width: 2
    })
  }),
  new OLStyleStyle({
    stroke: new OLStyleStroke({
      lineDash: [10, 10],
      color: 'black',
      width: 3
    })
  })],
  labelStyle: new OLStyleStyle({
    text: new OLStyleText({
      font: '500 14px Roboto, sans-serif',
      fill: new OLStyleFill({ color: 'white' }),
      backgroundFill: new OLStyleFill({ color: 'black' }),
      padding: [3, 3, 3, 3],
      textBaseline: 'bottom',
      offsetY: -15
    }),
    image: new OLStyleRegularShape({
      radius: 8,
      points: 3,
      angle: Math.PI,
      displacement: [0, 8],
      fill: new OLStyleFill({ color: 'black' })
    })
  }),
  modifyStyle: new OLStyleStyle({
    image: new OLStyleCircle({
      radius: 6,
      stroke: new OLStyleStroke({ color: 'white' }),
      fill: new OLStyleFill({ color: 'rgb(211, 47, 47)' })
    }),
    text: new OLStyleText({
      text: 'Click & drag to modify the measurement',
      font: '12px Roboto, sans-serif',
      fill: new OLStyleFill({ color: 'white' }),
      backgroundFill: new OLStyleFill({ color: 'rgba(0, 0, 0, 0.6)' }),
      padding: [2, 2, 2, 2],
      textAlign: 'left',
      offsetX: 15
    })
  }),
  segmentStyle: new OLStyleStyle({
    text: new OLStyleText({
      fill: new OLStyleFill({ color: 'black' }),
      font: '500 14px Roboto, sans-serif',
      stroke: new OLStyleStroke({ color: 'white', width: 2 }),
      offsetY: -8,
      overflow: true,
      placement: 'line'
    })
  }),
  snapStyle: new OLStyleStyle({
    image: new OLStyleCircle({
      radius: 6,
      stroke: new OLStyleStroke({ color: 'white' }),
      fill: new OLStyleFill({ color: 'rgb(46, 125, 50)' })
    })
  }),
  tooltipStyle: new OLStyleStyle({
    text: new OLStyleText({
      text: 'Click to start measuring',
      font: '12px Roboto, sans-serif',
      fill: new OLStyleFill({ color: 'white' }),
      backgroundFill: new OLStyleFill({ color: 'rgba(0, 0, 0, 0.6)' }),
      padding: [2, 2, 2, 2],
      textAlign: 'left',
      offsetX: 15
    })
  })
}

const measurementTips: MeasurementToolTips = {
  activeTip: 'Click to continue measuring\nDouble-click to finish',
  idleTip: 'Click to start measuring'
}

interface ToolsPanelMeasureProps {
  toggleExpansionPanel: () => void
}

const ToolsPanelMeasure: React.FC<ToolsPanelMeasureProps> = ({ toggleExpansionPanel }) => {
  const tools = useSelector((state: RootState) => state?.config?.layout?.drawer?.tabs?.tools)

  const expanded = tools?.measure?.expanded ?? false
  const title = tools?.measuregroup?.title ?? 'Measure'

  const [measurementTool, setMeasurementTool] = useState<MeasurementTool | null>(null)
  const [measurementsExist, setMeasurementsExist] = useState(false)
  const [measurementType, setMeasurementType] = useState('')
  const [showSegmentLengths, setShowSegmentLengths] = useState(true)
  const [showTotalMeasurement, setShowTotalMeasurement] = useState(true)
  const [snapToFeatures, setSnapToFeatures] = useState(true)

  const onMeasurementComplete = () => {
    setMeasurementsExist(true)
  }

  // When the user changes tabs on the panel, end measuring
  useEffect(() => {
    Model.on('app.state.tab', () => {
      finishMeasurement()
    })
  }, [])

  // When the measuring tools are hidden in the accordion menu, end measuring
  useEffect(() => {
    if (!expanded) {
      finishMeasurement()
    }
  }, [expanded])

  // If any measurement tool settings are changed this is run.  The first time a measurement tool (distance or area)
  // is selected a new instance of the MeasurementTool class is created, and default mouse interactions are disabled.
  // Further changes result in the MeasurementTool instance being updated.
  useEffect(() => {
    if (!measurementType && !measurementTool) return

    if (measurementTool) {
      measurementTool.updateMeasurementOptions({
        measurementType,
        showSegmentLengths,
        showTotalMeasurement,
        snapToFeatures
      })

      return
    }

    setMeasurementTool(
      new MeasurementTool({
        measurementType,
        showSegmentLengths,
        showTotalMeasurement,
        snapToFeatures,
        styles: measurementStyles,
        toolTips: measurementTips,
        onMeasurementComplete
      })
    )
  }, [measurementType, showSegmentLengths, showTotalMeasurement, snapToFeatures])

  const clearMeasurements = () => {
    setMeasurementsExist(false)
    measurementTool?.clear()
  }

  const finishMeasurement = () => {
    setMeasurementType('')
  }

  const headerIcon = <img alt="Distance" src={measureDistanceImg} />

  const measureDistanceButton = (
    <ToggleButton
      onClick={() => measurementType === 'distance' ? setMeasurementType('') : setMeasurementType('distance')}
      selected={measurementType === 'distance'}
      value='distance'
    >
      <img alt="Length/Distance" src={measureDistanceImg} />
      Length/Distance
    </ToggleButton>
  )

  const measureAreaButton = (
    <ToggleButton
      onClick={() => measurementType === 'area' ? setMeasurementType('') : setMeasurementType('area')}
      selected={measurementType === 'area'}
      value='area'
    >
      <img alt="Area" src={measureAreaImg} />
      Area/Region
    </ToggleButton>
  )

  const measurementOptions = (
    <Stack style={styles.checkBoxContainer} direction="column">
      <FormControlLabel
        control={<Checkbox checked={showSegmentLengths} onChange={(e) => setShowSegmentLengths(e.target.checked)} />}
        label='Show segment lengths'
      />
      <FormControlLabel
        control={<Checkbox checked={showTotalMeasurement} onChange={(e) => setShowTotalMeasurement(e.target.checked)} />}
        label='Show total measurement'
      />
      <Stack alignContent="center" direction="row">
        <FormControlLabel
          control={<Checkbox checked={snapToFeatures} onChange={(e) => setSnapToFeatures(e.target.checked)}/>}
          label='Snap to features'
        />
        <Tooltip title="Snap measurements to nearby features in any active vector layer">
          <Help style={styles.helpButton} />
        </Tooltip>
      </Stack>
    </Stack>
  )

  const clearAllButton = (
    <Button color="error" onClick={clearMeasurements}>
      <RubbishBinIcon />
      Clear all
    </Button>
  )

  const measurementFinishButton = (
    <Button onClick={finishMeasurement}>
      <CheckIcon />
      Finish
    </Button>
  )

  return (
    <AccordionWrapper
      licensed={true}
      expanded={expanded}
      onChange={toggleExpansionPanel}
      headerIcon={headerIcon}
      title={title}
      bExcludeExpandIcon={false}
    >
      <Stack style={styles.root}>
        {measureDistanceButton}
        {measureAreaButton}
        {measurementType && measurementOptions}
        {measurementsExist && clearAllButton}
        {measurementType && measurementFinishButton}
      </Stack>
    </AccordionWrapper>
  )
}

export default ToolsPanelMeasure
